import React, { useEffect, memo, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { PropTypes } from "prop-types";
import reactWrapper from "@harbor/elements/utils/react/wrapper";

import css from "../../reportsMagneticStyle.less";
import { useI18n } from "../../../../i18n/useI18n";

import imgCsv from "../../../../assets/file-csv.svg";
import imgPdf from "../../../../assets/file-pdf.svg";

import AppHoc from "../../../../generics/AppHOC";
import Spinner from "../../../../common/Spinner";
import NoDataAvailable from "../../../../common/NoDataAvailable";
import * as actions from "../actions";
import { FEATURE_CONFIG } from "../../../../utils/enums";
import BetaTag from "../../../../common/BetaTag";
import ReportTemplateDrawer from "./ReportTemplateDrawer";

const [HbrCard, HbrTooltip, HbrIconButton, HbrButton] = reactWrapper([
  "hbr-card",
  "hbr-tooltip",
  "hbr-icon-button",
  "hbr-button",
]);

const mapStateToProps = (state) => {
  return {
    reports: state.vanalytics.reports,
  };
};
const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(actions, dispatch),
});

const ReportTemplates = (props) => {
  const { tViews } = useI18n("views");
  const { actions, reports, globalFilter } = props;
  const { selectedOverlay, currentTimeStamp } = globalFilter;
  const { reportTemplates, reportTemplateLoading } = reports;
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // File type to icon mapping
  const fileTypeToIcon = {
    pdf: { src: imgPdf, alt: "PDF File" },
    csv: { src: imgCsv, alt: "CSV File" },
  };

  // Function to validate and retrieve icon data for all valid file types
  const validateFileTypes = (fileType) => {
    if (!fileType) return [];
    // Split the fileType string into an array
    const fileTypes = fileType.split(",").map((type) => type.trim());
    // Filter out invalid file types and map to icon data
    return fileTypes
      .filter((type) => fileTypeToIcon[type])
      .map((type) => fileTypeToIcon[type]);
  };

  useEffect(() => {
    const { getReportTemplates } = actions;
    getReportTemplates();
  }, [selectedOverlay, currentTimeStamp && currentTimeStamp]);

  const closeTemplateDrawer = () => {
    setSelectedTemplate(null);
  };

  if (reportTemplateLoading) {
    return <Spinner />;
  } else {
    if (reportTemplates && reportTemplates.length > 0) {
      return (
        <div className={css["report-template"]}>
          {reportTemplates.map((reportTemplate, index) => {
            const { template_id, template_name, include_reports, file_type } =
              reportTemplate;
            const cardName = `card${template_id}`;
            const includeReports = include_reports.split(",");
            const iconsData = validateFileTypes(file_type);
            return (
              <div key={index}>
                <HbrCard name={cardName} data-cy={template_name}>
                  <div slot="header">
                    <div className="reports-title-icons-container">
                      <div role="heading" aria-level={3}>
                        {template_name}
                        {FEATURE_CONFIG.LINKSLA.isBeta === true &&
                          template_name === "Link SLA" && (
                            <div className="reports-beta" slot="suffix">
                              <BetaTag />
                            </div>
                          )}
                        {FEATURE_CONFIG.LINKUTILIZATION.isBeta === true &&
                          template_name === "Link Utilization" && (
                            <div className="reports-beta" slot="suffix">
                              <BetaTag />
                            </div>
                          )}
                        {FEATURE_CONFIG.LINK_AVAILABILITY.isBeta === true &&
                          template_name === "Link Availability" && (
                            <div className="reports-beta" slot="suffix">
                              <BetaTag />
                            </div>
                          )}
                        {FEATURE_CONFIG.LINK_SUMMARY.isBeta === true &&
                          template_name === "Link Summary" && (
                            <div className="reports-beta" slot="suffix">
                              <BetaTag />
                            </div>
                          )}
                      </div>
                      <div className="reports-icons">
                        {iconsData.length > 0 ? (
                          iconsData.map((icon, index) => (
                            <img key={index} src={icon.src} alt={icon.alt} />
                          ))
                        ) : (
                          <HbrTooltip
                            content={tViews(
                              "reportTemplatesNoFileTypeAvailable",
                            )}
                            placement="top"
                          >
                            <HbrIconButton
                              name="info"
                              sentiment="neutral"
                              arialabel={`${tViews("circuit360Tooltip.tooltip")}`}
                            />
                          </HbrTooltip>
                        )}
                      </div>
                    </div>
                    <div className="reports-list">
                      <ul>
                        {includeReports &&
                          includeReports.length > 0 &&
                          includeReports.map((report, idx) => (
                            <li key={idx}>{report}</li>
                          ))}
                      </ul>
                    </div>
                  </div>
                  <div>
                    <HbrButton
                      variant="text"
                      data-cy={`Generate ${template_name} btn`}
                      onClick={() => setSelectedTemplate(reportTemplate)}
                    >
                      {tViews("reportTemplatesGenerate")}
                    </HbrButton>
                  </div>
                </HbrCard>
              </div>
            );
          })}
          {selectedTemplate && (
            <ReportTemplateDrawer
              template={selectedTemplate}
              onClose={closeTemplateDrawer}
              onGenerated={() => actions.getReportsList?.(selectedOverlay)}
            />
          )}
        </div>
      );
    } else {
      if (reportTemplates && reportTemplates.length === 0) {
        return <NoDataAvailable />;
      } else {
        return <Spinner />;
      }
    }
  }
};

ReportTemplates.propTypes = {
  globalFilter: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  reports: PropTypes.object.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(memo(AppHoc(ReportTemplates)));
